import type { Suggestion, SuggestionSource } from '../../aiSuggestionsPluginType';

/**
 * CSS anchor names the follow-up card positions itself against. Fixed rather than
 * localId-interpolated like the suggestion card's, since at most one follow-up card exists at
 * a time — but distinct from `--card-*` / `--button-*` so both sets can sit in the DOM.
 */
export const FOLLOW_UP_CARD_ANCHOR_NAMES: {
	base: string;
	end: string;
	outerEnd: string;
	outerStart: string;
	start: string;
} = {
	base: '--followup-card-base',
	end: '--followup-card-end',
	// Outward fallbacks, emitted only when the primary pair sits inside the target node. See
	// `findSuggestionCardAnchorRanges`.
	outerEnd: '--followup-card-outer-end',
	outerStart: '--followup-card-outer-start',
	start: '--followup-card-start',
};

/**
 * Keyed off `markerRef` (the comment's document anchor), which only inline comments carry.
 * The source `type` isn't checked because its value isn't stable — the backend has been seen
 * sending `confluence.comment` where `confluence:comment` was specified.
 */
const isInlineCommentSource = (source: SuggestionSource): boolean =>
	!!source.metadata && 'markerRef' in source.metadata;

const isInlineCommentSuggestion = (suggestion: Suggestion): boolean =>
	suggestion.sources?.some(isInlineCommentSource) ?? false;

/**
 * Whether removing `suggestion` leaves a follow-up card behind: inline-comment sources only.
 * Half the condition — the `REMOVE_SUGGESTION` reducer adds the rest, that the removal also
 * empties the list.
 *
 * The card can therefore be offered more than once per user. Suppressing it after the first
 * time needs somewhere to persist that, which is deliberately left to a follow-up.
 */
export const shouldShowFollowUpCard = (suggestion: Suggestion | undefined): boolean =>
	suggestion !== undefined && isInlineCommentSuggestion(suggestion);
